import { produce } from 'immer';
import { useMemo, useState } from 'react';
import type {
  TreePickerItem,
  TreePickerItemWithValue,
} from './helpers/TreePickerItem';
import { filterSelectedTreePickerValues } from './helpers/filterSelectedTreePickerValues';
import { mapCheckboxValuesToTree } from './helpers/mapCheckboxValuesToTree';
import { searchTreePickerItems } from './helpers/searchTreePickerItems';

interface TreePickerSearch<TKey> {
  value: string;
  handleChange: (value: string) => void;
  expandedNodes: Set<TKey>;
}

interface TreePickerItemsHook<TKey> {
  availableItems: TreePickerItemWithValue<TKey>[];
  selectedItems: TreePickerItemWithValue<TKey>[];
  availableSearch: TreePickerSearch<TKey>;
  selectedSearch: TreePickerSearch<TKey>;
}
interface Props<TKey> {
  items: TreePickerItem<TKey>[];
  value: Set<TKey>;
}

export const useTreePickerItems = <TKey extends string>(
  props: Props<TKey>,
): TreePickerItemsHook<TKey> => {
  const [availableSearchValue, setAvailableSearchValue] = useState<string>('');
  const [selectedSearchValue, setSelectedSearchValue] = useState<string>('');

  const itemsWithValue = useMemo(
    () => mapCheckboxValuesToTree(props.items, props.value),
    [props.value, props.items],
  );

  const selectedItemsWithValue = useMemo(() => {
    // have to wrap items to object bc immer don't allow change & return of new value
    // at same time
    const result = produce({ items: itemsWithValue }, (draft) => {
      draft.items = draft.items.filter(filterSelectedTreePickerValues);
    });

    return result.items;
  }, [itemsWithValue]);

  const [availableItems, availableExpandedNodes] = useMemo(() => {
    return searchTreePickerItems(
      itemsWithValue,
      availableSearchValue.toLowerCase(),
    );
  }, [availableSearchValue, itemsWithValue]);

  const [selectedItems, selectedExpandedNodes] = useMemo(() => {
    return searchTreePickerItems(
      selectedItemsWithValue,
      selectedSearchValue.toLowerCase(),
    );
  }, [selectedSearchValue, selectedItemsWithValue]);

  return {
    availableItems,
    selectedItems,
    availableSearch: {
      value: availableSearchValue,
      handleChange: setAvailableSearchValue,
      expandedNodes: availableExpandedNodes,
    },
    selectedSearch: {
      value: selectedSearchValue,
      handleChange: setSelectedSearchValue,
      expandedNodes: selectedExpandedNodes,
    },
  };
};
